import { Megaphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getBannerRotation, recordBannerClick, recordBannerImpressions } from '@/api/member';

/**
 * BannerAdSlot — stack-of-N 468×60 banner display.
 *
 * - Pure-random rotation per page render: every mount = new GET to
 *   /api/public/banner-rotation (no caching, no timestamp ordering).
 * - Fires ONE impression-record POST after mount for the served IDs.
 * - Click → records click → opens destination in a new tab with
 *   rel="noopener noreferrer nofollow" (industry-standard ad-link hardening).
 * - Gracefully renders a "Your Ad Here" house-promo card when fewer than
 *   N active ads are available.
 */
const BANNER_W = 468;
const BANNER_H = 60;

/**
 * The slot's box: the banner's own size on wide screens, shrunk to the screen width on phones with
 * the same proportions — a fixed pixel width cut both sides of the banner off on mobile.
 */
const slotBox = (width, height) => ({ width: '100%', maxWidth: width, aspectRatio: `${width} / ${height}` });

const HousePromo = ({ width, height }) => (
  <a
    href="/dashboard?subpage=advertise"
    className="sc-glass relative overflow-hidden rounded-md flex items-center justify-center group"
    style={slotBox(width, height)}
    data-testid="banner-house-promo"
  >
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: 'linear-gradient(90deg, rgba(255,182,18,0.18), rgba(255,194,14,0.05) 60%, transparent)',
      }}
    />
    <div className="relative z-10 flex items-center gap-3 text-[12px] font-semibold tracking-wide text-[#FFE08A] px-3">
      <Megaphone className="w-4 h-4 text-[#FFC20E]" />
      <span className="truncate">Your Ad Here · Earn 1 ad credit per cent spent</span>
    </div>
  </a>
);

const BannerImg = ({ banner, width, height }) => {
  const handleClick = async (e) => {
    e.preventDefault();
    try {
      await recordBannerClick(banner.id);
    } catch {
      /* non-critical */
    }
    window.open(banner.destination_url, '_blank', 'noopener,noreferrer');
  };
  return (
    <a
      href={banner.destination_url}
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="rounded-md overflow-hidden block transition hover:opacity-95"
      style={slotBox(width, height)}
      data-testid={`banner-served-${banner.id}`}
      aria-label="Sponsored banner ad"
    >
      <img
        src={banner.image_url}
        alt="Sponsored"
        width={width}
        height={height}
        // `contain`: the whole banner is always visible, even one uploaded at slightly different proportions.
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </a>
  );
};

const BannerAdSlot = ({ slot = 'home', count = 2 }) => {
  const [banners, setBanners] = useState(null); // null = loading, [] = empty
  const [w, setW] = useState(BANNER_W);
  const [h, setH] = useState(BANNER_H);

  // Mount: fetch fresh random rotation. NO interval — next page load picks again.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await getBannerRotation(count);
        if (!alive) return;
        const arr = Array.isArray(r?.banners) ? r.banners : [];
        setBanners(arr);
        if (r?.width) setW(Number(r.width));
        if (r?.height) setH(Number(r.height));
        // Fire impressions once for the served IDs.
        const ids = arr.map((a) => a.id).filter(Boolean);
        if (ids.length > 0) {
          recordBannerImpressions(ids).catch(() => {});
        }
      } catch {
        if (alive) setBanners([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [count]);

  if (banners === null) {
    // Loading skeleton at the exact reserved size to prevent layout shift.
    return (
      <div className="w-full flex flex-col items-center gap-2 py-3" data-testid={`banner-slot-${slot}-loading`} aria-hidden="true">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="rounded-md"
            style={{
              ...slotBox(w, h),
              background: 'linear-gradient(90deg, rgba(255,182,18,0.04), rgba(255,182,18,0.08), rgba(255,182,18,0.04))',
            }}
          />
        ))}
      </div>
    );
  }

  // Compose final render: served banners + house promos to fill up to ``count``.
  const filled = banners.slice(0, count);
  const promosNeeded = Math.max(0, count - filled.length);

  return (
    <section className="w-full flex flex-col items-center gap-2 py-3" data-testid={`banner-slot-${slot}`} aria-label="Sponsored banner advertising">
      {filled.map((b) => (
        <BannerImg key={b.id} banner={b} width={w} height={h} />
      ))}
      {Array.from({ length: promosNeeded }).map((_, i) => (
        <HousePromo key={`promo-${i}`} width={w} height={h} />
      ))}
    </section>
  );
};

export default BannerAdSlot;
